import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { isAxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  type ConnectorLookupResponse,
  type ConnectorOccurrence,
  createConnectorLookupSession,
  fetchConnectorLookup,
} from 'features/knowledgeDocumentViewer/utils/connectorLookup'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

export type ConnectorLookupState = {
  label: string
  loading: boolean
  error?: string
  response?: ConnectorLookupResponse
}

function errorMessage(error: Error): string {
  if (
    isAxiosError(error) &&
    (error.response?.status === 504 || error.code === 'ECONNABORTED')
  ) {
    return t`Finding the destination timed out. Try again.`
  }
  if (isAxiosError(error) && error.response?.status === 409) {
    return t`Document configuration changed. Reload and try again.`
  }
  return t`Could not look up the destination. Try again.`
}

export function useConnectorLookup(
  siteId: string,
  documentId: string,
  pageNumber: number,
  userId: string | undefined,
  active: boolean
) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const { selectedPipelineConfigId, isPreviewMode } = usePipelineConfig()
  const [state, setState] = useState<ConnectorLookupState>()
  const pending = useRef<Promise<ConnectorLookupResponse | undefined>>()
  const lastOccurrence = useRef<ConnectorOccurrence>()
  const generation = useRef(0)
  const scope = `${userId}:${siteId}:${selectedPipelineConfigId}:${documentId}:${pageNumber}:${active}`
  const session = useMemo(
    () =>
      createConnectorLookupSession(
        (request, signal) =>
          fetchConnectorLookup(axios, POWER_BACKEND_AI_URL, request, signal),
        scope
      ),
    [axios, POWER_BACKEND_AI_URL, scope]
  )
  const cancel = useCallback(() => {
    generation.current += 1
    session.cancel()
    pending.current = undefined
    setState(undefined)
  }, [session])

  useLayoutEffect(() => {
    cancel()
    lastOccurrence.current = undefined
    return () => {
      generation.current += 1
      session.cancel()
    }
  }, [cancel, session])

  const lookup = useCallback(
    async (occurrence: ConnectorOccurrence) => {
      if (!active || !selectedPipelineConfigId || !userId) return undefined
      const task = session.run({
        ...occurrence,
        site_id: siteId,
        source_document_id: documentId,
        pipeline_config_id: selectedPipelineConfigId,
        preview_mode: isPreviewMode,
      })
      if (pending.current === task) return undefined
      const currentGeneration = ++generation.current
      pending.current = task
      lastOccurrence.current = occurrence
      setState({ label: occurrence.raw_text, loading: true })
      try {
        const response = await task
        if (!response || generation.current !== currentGeneration)
          return undefined
        setState({ label: occurrence.raw_text, loading: false, response })
        return response
      } catch (error) {
        if (generation.current === currentGeneration) {
          setState({
            label: occurrence.raw_text,
            loading: false,
            error: errorMessage(error instanceof Error ? error : new Error()),
          })
        }
        return undefined
      } finally {
        if (generation.current === currentGeneration)
          pending.current = undefined
      }
    },
    [
      active,
      selectedPipelineConfigId,
      userId,
      session,
      siteId,
      documentId,
      isPreviewMode,
    ]
  )

  return { state, lookup, cancel, lastOccurrence }
}
